import React, { useState, useEffect } from 'react';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import { AlertTriangle, Bell, Clock, CheckCircle, Send, Mail, Building2, Calendar, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Composant de gestion des alertes automatiques pour les virements
 * Affiche le statut et permet de déclencher manuellement les alertes
 */
export default function VirementAlertsManager() {
  const { getAuthHeader } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, configRes] = await Promise.all([
        axios.get(`${API}/api/virement-alerts/status`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/virement-alerts/config`, { headers: getAuthHeader() })
      ]);
      setStatus(statusRes.data);
      setConfig(configRes.data);
    } catch {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAlert = async () => {
    setTriggering(true);
    try {
      const response = await axios.post(
        `${API}/api/virement-alerts/trigger`,
        {},
        { headers: getAuthHeader() }
      );
      
      const result = response.data.result;
      if (result.alerts_sent > 0) {
        toast.success(`✅ ${result.alerts_sent} alerte(s) envoyée(s) à ${result.recipients?.length || 0} admin(s)`);
      } else {
        toast.info('Aucune alerte à envoyer - tous les virements sont récents ou déjà alertés');
      }
      
      // Rafraîchir les données
      await fetchData();
    } catch {
      toast.error('Erreur lors de l\'envoi des alertes');
    } finally {
      setTriggering(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const hasVirementsToAlert = status?.pending_over_48h > 0;

  return (
    <div className="space-y-6" data-testid="virement-alerts-manager">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Bell className="h-6 w-6" />
            Alertes Virements Automatiques
          </CardTitle>
          <CardDescription className="text-white/90">
            Notifications automatiques pour les virements en attente depuis +48h
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Statut actuel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Virements en attente total */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Virements en attente</p>
                <p className="text-3xl font-bold text-blue-600">{status?.pending_virements_total || 0}</p>
              </div>
              <Building2 className="h-10 w-10 text-blue-500 opacity-30" />
            </div>
          </CardContent>
        </Card>

        {/* Virements +48h */}
        <Card className={`border-l-4 ${hasVirementsToAlert ? 'border-l-red-500 bg-red-50' : 'border-l-green-500'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En attente +48h</p>
                <p className={`text-3xl font-bold ${hasVirementsToAlert ? 'text-red-600' : 'text-green-600'}`}>
                  {status?.pending_over_48h || 0}
                </p>
                {hasVirementsToAlert && (
                  <p className="text-xs text-red-500 mt-1">Alerte recommandée</p>
                )}
              </div>
              {hasVirementsToAlert ? (
                <AlertTriangle className="h-10 w-10 text-red-500 opacity-50" />
              ) : (
                <CheckCircle className="h-10 w-10 text-green-500 opacity-30" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Seuil d&apos;alerte</p>
                <p className="text-3xl font-bold text-purple-600">{config?.alert_threshold_hours || 48}h</p>
                <p className="text-xs text-gray-500">Rappel: {config?.reminder_interval_hours || 24}h</p>
              </div>
              <Clock className="h-10 w-10 text-purple-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Actions
            </CardTitle>
            <RefreshButton
              onRefresh={fetchData}
              loading={loading}
              variant="ghost"
              testId="virement-alerts-refresh-btn"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Envoyer les alertes maintenant</p>
              <p className="text-sm text-gray-500">
                {hasVirementsToAlert 
                  ? `${status?.pending_over_48h} virement(s) seront signalé(s) aux administrateurs`
                  : 'Aucun virement ne nécessite d\'alerte actuellement'
                }
              </p>
            </div>
            <Button 
              onClick={handleTriggerAlert}
              disabled={triggering || !hasVirementsToAlert}
              className={hasVirementsToAlert ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {triggering ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {triggering ? 'Envoi en cours...' : 'Envoyer les alertes'}
            </Button>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Planification automatique</p>
                <p className="text-sm text-blue-700">
                  Les alertes sont envoyées automatiquement à <strong>10h00</strong> et <strong>16h00</strong> chaque jour
                  si des virements sont en attente depuis plus de {config?.alert_threshold_hours || 48} heures.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des virements en attente */}
      {status?.virements_needing_alert?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Virements nécessitant une alerte ({status.virements_needing_alert.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {status.virements_needing_alert.map((v, index) => (
                <div 
                  key={v.id || index}
                  className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{v.titre || 'Convention sans titre'}</p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Réf: {v.reference || '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-red-500" />
                        {v.wait_days} jours d&apos;attente
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{formatMoney(v.montant)}</p>
                    <Badge variant="destructive" className="mt-1">
                      En attente
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message si tout est OK */}
      {!hasVirementsToAlert && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <div>
                <p className="text-lg font-semibold text-green-800">Tout est à jour !</p>
                <p className="text-green-700">
                  Aucun virement n&apos;est en attente depuis plus de {config?.alert_threshold_hours || 48} heures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
